import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Card, { CardBody, CardHeader } from "../components/ui/Card/Card";
import ProductGallery from "../components/ui/ProductGallery/ProductGallery";
import ProductModal from "../components/ui/ProductModal/ProductModal";
 
import { PRODUCT_CATEGORIES } from "../data/products";
import { getImagesByCategory } from "../data/productImages";
import "./ProductsPage.css";
import { listProducts } from "../services/firebase";
import type { Product } from "../services/firebase";
import { useLanguage } from "../context/LanguageContext";


const ProductsPage: React.FC = () => {
  
  const [params] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [initialIndex, setInitialIndex] = useState(0);
  const categoryId = params.get('category') || '';
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<'recent' | 'name' | 'category'>('recent');
  const pageSize = 12;
  const [page, setPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const { t } = useLanguage();

  useEffect(() => {
    (async () => {
      try {
        const data = await listProducts();
        setProducts(data as Product[]);
        setLoading(false);
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    })();
  }, []);

  const mappedImages = useMemo(() => {
    if (!selectedCategoryId) return [];
    const imgs = getImagesByCategory(selectedCategoryId);
    return imgs.map((img: any) => ({
      id: img.id,
      src: img.image,
      alt: img.name,
      title: img.name,
      category: img.category,
    }));
  }, [selectedCategoryId]);

  // Sade filtreleme: seçili kategoriye göre ürünleri göster
  const normalized = (s?: string | null) => (s || '').toLowerCase().trim();
  const CATEGORY_ICONS: Record<string, string> = {
    'motor-yenileme-ekipmanlari': 'fas fa-tools',
    'motor-yenileme-makinalari': 'fas fa-cogs',
    'motor-yenileme-yedek-parcalari': 'fas fa-puzzle-piece',
    'otomotiv-servis-elemanlari': 'fas fa-wrench',
    'ikinci-el-satilik-urunler': 'fas fa-recycle',
  };

  const CATEGORY_ALIASES: Record<string, string[]> = {
    'motor-yenileme-makinalari': ['machines', 'makina', 'makine'],
    'motor-yenileme-yedek-parcalari': ['spare-parts', 'yedek parça', 'yedek'],
    'otomotiv-servis-elemanlari': ['service-equipment', 'servis', 'ekipman'],
    'ikinci-el-satilik-urunler': ['second-hand', 'ikinci el', '2. el'],
    'motor-yenileme-ekipmanlari': ['equipment', 'ekipman', 'yenileme'],
  };

  const selectedCategoryMeta = useMemo(() => {
    const found = PRODUCT_CATEGORIES.find((c: any) => c.id === selectedCategoryId);
    if (!found) return { title: '', names: [] as string[] };
    const top = found.name;
    const subs = (found.subcategories || []).map((s: any) => s.name).filter(Boolean);
    const aliases = CATEGORY_ALIASES[selectedCategoryId] || [];
    return { title: normalized(top), names: [top, ...subs, ...aliases] };
  }, [selectedCategoryId]);

  const filteredProducts = useMemo(() => {
    const activeOnly = products.filter(p => (p as any).active !== false);
    const base = selectedCategoryId
      ? activeOnly.filter(p => {
          const pc = normalized(p.category);
          if (selectedSubCategoryId) {
            const foundCat = PRODUCT_CATEGORIES.find((c: any) => c.id === selectedCategoryId);
            const foundSub = foundCat?.subcategories?.find((s: any) => s.id === selectedSubCategoryId);
            if (foundSub) {
              return pc.includes(normalized(foundSub.name));
            }
          }
          return selectedCategoryMeta.names.some(n => pc.includes(normalized(n)));
        })
      : activeOnly;
    const bySearch = search
      ? base.filter(p => normalized(p.name).includes(normalized(search)) || normalized(p.description).includes(normalized(search)))
      : base;
    const sorted = [...bySearch].sort((a, b) => {
      if (sortKey === 'name') return normalized(a.name).localeCompare(normalized(b.name));
      if (sortKey === 'category') return normalized(a.category).localeCompare(normalized(b.category));
      const ad = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bd = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bd - ad; // recent first
    });
    return sorted;
  }, [products, selectedCategoryId, selectedCategoryMeta, search, sortKey, selectedSubCategoryId]);

  const visibleProducts = useMemo(() => filteredProducts.slice(0, page * pageSize), [filteredProducts, page, pageSize]);


  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const activeOnly = products.filter(p => (p as any).active !== false);
    PRODUCT_CATEGORIES.forEach((c: any) => {
      const names: string[] = [c.name, ...((c.subcategories || []).map((s: any) => s.name) || [])].filter(Boolean);
      counts[c.id] = activeOnly.filter(p => {
        const pc = normalized(p.category);
        return names.some(n => pc.includes(normalized(n)));
      }).length;

      // Subcategory counts
      if (c.subcategories) {
        c.subcategories.forEach((s: any) => {
          counts[s.id] = activeOnly.filter(p => normalized(p.category).includes(normalized(s.name))).length;
        });
      }
    });
    return counts;
  }, [products]);

  return (
    <section className="products-page">
      <div className="container">
        {/* Breadcrumbs */}
        <nav className="breadcrumbs" aria-label="breadcrumb">
          <ol>
            <li><a href="/">{t('nav.home')}</a></li>
            <li aria-current="page">{t('nav.products')}</li>
          </ol>
        </nav>

        <div className="products-layout">
          <aside className="products-sidebar">
            <div className="products-filter">
              <button
                className={`chip ${!selectedCategoryId ? 'chip--active' : ''}`}
                onClick={() => { setSelectedCategoryId(''); setSelectedSubCategoryId(''); setPage(1); }}
              >
                {t('products.filter.all')} ({products.length})
              </button>
              {PRODUCT_CATEGORIES.map((c: any) => (
                <React.Fragment key={c.id}>
                  <button
                    className={`chip ${selectedCategoryId === c.id ? 'chip--active' : ''}`}
                    onClick={() => {
                      if (selectedCategoryId === c.id) {
                         setSelectedCategoryId('');
                         setSelectedSubCategoryId('');
                      } else {
                         setSelectedCategoryId(c.id);
                         setSelectedSubCategoryId('');
                      }
                      setPage(1);
                    }}
                  >
                    <i className={`chip__icon ${CATEGORY_ICONS[c.id] || 'fas fa-tags'}`}></i>
                    {c.name} ({categoryCounts[c.id] || 0})
                  </button>
                  {selectedCategoryId === c.id && c.subcategories && c.subcategories.length > 0 && (
                    <div className="subcategory-list">
                      {c.subcategories.map((s: any) => (
                        <button
                          key={s.id}
                          className={`chip chip--sm ${selectedSubCategoryId === s.id ? 'chip--active-sub' : ''}`}
                          onClick={() => { setSelectedSubCategoryId(selectedSubCategoryId === s.id ? '' : s.id); setPage(1); }}
                        >
                          {s.name} ({categoryCounts[s.id] || 0})
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
              {selectedCategoryId && (
                <button
                  className="chip chip--ghost"
                  onClick={() => {
                    setGalleryImages(mappedImages);
                    setInitialIndex(0);
                    setGalleryOpen(true);
                  }}
                >
                  {t('products.categoryGallery')}
                </button>
              )}
            </div>
          </aside>

          <section className="products-content">
            <div className="products-toolbar">
              <input
                type="text"
                className="search-input"
                placeholder={t('products.search.placeholder')}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              <select
                className="sort-select"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as any)}
              >
                <option value="recent">{t('sort.recent')}</option>
                <option value="name">{t('sort.name')}</option>
                <option value="category">{t('sort.category')}</option>
              </select>
              <div className="view-toggle" role="group" aria-label={t('aria.viewMode')}>
                <button className={`view-btn ${viewMode === 'grid' ? 'view-btn--active' : ''}`} onClick={() => setViewMode('grid')} title={t('view.grid')}>
                  <i className="fas fa-th"></i>
                </button>
                <button className={`view-btn ${viewMode === 'list' ? 'view-btn--active' : ''}`} onClick={() => setViewMode('list')} title={t('view.list')}>
                  <i className="fas fa-list"></i>
                </button>
              </div>
              <div className="results-info">
                {filteredProducts.length} {t('results.label')}
              </div>
            </div>

            <div className="products-grid">
              {loading && (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={`skeleton-${i}`} className="product-card product-card--skeleton">
                    <div className="product-thumb skeleton" />
                    <div className="skeleton skeleton--text" />
                    <div className="skeleton skeleton--text short" />
                  </div>
                ))
              )}
              {!loading && visibleProducts.map((p) => (
                viewMode === 'grid' ? (
                  <Card key={p.id} variant="outlined" hover className="product-card">
                    <CardBody>
                      <div className="product-thumb">
                        {p.image_url ? (
                          <img src={`${p.image_url}`} alt={p.name} loading="lazy" decoding="async" sizes="(max-width: 480px) 160px, (max-width: 768px) 220px, 280px" />
                          ) : (
                          <div className="product-thumb__placeholder" />
                          )}
                      </div>
                      <h4 className="product-title">{p.name}</h4>
                      {p.category && <p className="product-category">{p.category}</p>}
                      
                      {p.description && <p className="product-desc">{p.description}</p>}
                      <div className="product-actions">
                        <button className="button button--primary button--sm" onClick={() => setSelectedProduct(p)}>
                          {t('common.detail')}
                        </button>
                        <button
                          className="button button--outline button--sm"
                          onClick={() => {
                            const phone = '+905346690042'.replace(/\s+/g, '').replace('+', '');
                            const msg = encodeURIComponent(t('whatsapp.messageWithProduct').replace('{product}', p.name));
                            const url = `https://wa.me/${phone}?text=${msg}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          {t('contact.info.whatsapp')}
                        </button>
                      </div>
                    </CardBody>
                  </Card>
                ) : (
                  <Card key={p.id} variant="outlined" hover className="product-card product-card--list">
                    <CardBody>
                      <div className="product-list__row">
                        <div className="product-thumb product-thumb--list">
                          {p.image_url ? (
                            <img src={`${p.image_url}`} alt={p.name} loading="lazy" decoding="async" sizes="(max-width: 480px) 140px, 160px" />
                          ) : (
                          <div className="product-thumb__placeholder" />
                          )}
                        </div>
                        <div className="product-list__content">
                          <h4 className="product-title">{p.name}</h4>
                          {p.category && <p className="product-category">{p.category}</p>}
                          
                          {p.description && <p className="product-desc">{p.description}</p>}
                          <div className="product-actions">
                            <button className="button button--primary button--sm" onClick={() => setSelectedProduct(p)}>
                              {t('common.detail')}
                            </button>
                            <button
                              className="button button--outline button--sm"
                              onClick={() => {
                                const phone = '+905346690042'.replace(/\s+/g, '').replace('+', '');
                                const msg = encodeURIComponent(t('whatsapp.messageWithProduct').replace('{product}', p.name));
                                const url = `https://wa.me/${phone}?text=${msg}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                            >
                              {t('contact.info.whatsapp')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )
              ))}
              {visibleProducts.length === 0 && (
                <div className="empty-state">
                  <h3>{t('products.empty.title')}</h3>
                  <p>{t('products.empty.desc')}</p>
                  <div className="cta-buttons">
                    <a
                      className="button button--primary"
                      href={`https://wa.me/${'+905346690042'.replace(/\s/g,'').replace('+','')}?text=${encodeURIComponent(t('whatsapp.default'))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('contact.whatsapp.cta')}
                    </a>
                    <a className="button button--outline" href="/#contact">{t('contact.form.cta')}</a>
                  </div>
                </div>
              )}
            </div>

            {visibleProducts.length < filteredProducts.length && (
              <div className="load-more">
                <button className="btn-load-more" onClick={() => setPage(page + 1)}>{t('common.loadMore')}</button>
              </div>
          )}
        </section>
      </div>

        {/* Inline Kategori Galerisi */}
        {selectedCategoryId && (
          <div className="inline-gallery">
            <h3 className="section-subtitle">{t('products.categoryGallery')}</h3>
            <div className="inline-gallery__grid">
              {mappedImages.map((img: any, idx: number) => (
                <div
                  key={img.id}
                  className="inline-gallery__item"
                  onClick={() => {
                    setGalleryImages(mappedImages);
                    setInitialIndex(idx);
                    setGalleryOpen(true);
                  }}
                >
                  <img src={img.src} alt={img.alt} loading="lazy" decoding="async" />
                  <span className="inline-gallery__caption">{img.title}</span>
                </div>
              ))}
              {mappedImages.length === 0 && (
                <p className="inline-gallery__empty">{t('gallery.empty')}</p>
              )}
            </div>
          </div>
        )}

        {/* Statik Kategoriler */}
        <div className="products-categories">
          <h3 className="section-subtitle">{t('nav.categories')}</h3>
          <div className="categories-grid">
            {PRODUCT_CATEGORIES.map((category: any) => (
              <Card key={category.id} variant="default" hover>
                <CardHeader>
                  <strong>{category.name}</strong>
                </CardHeader>
                <CardBody>
                  <p className="category-desc">{category.description}</p>
                </CardBody>
              </Card>
            ))}
          </div>
        </div>

        <ProductGallery
          images={galleryImages}
          isOpen={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          initialIndex={initialIndex}
        />
        <ProductModal
          product={selectedProduct}
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      </div>
    </section>
  );
};

export default ProductsPage;
